/**
 * Swipeable horizontal carousel of recent exposure log entries
 * Crew-focused: swipe to browse history, tap for details
 */

import React, { useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  FlatList,
  Dimensions,
  Platform,
} from 'react-native';
import { format } from 'date-fns';
import type { ExposureLogEntry } from '../types';

const CARD_WIDTH = Math.min(280, Dimensions.get('window').width - 48);
const CARD_MARGIN = 12;
const ITEM_WIDTH = CARD_WIDTH + CARD_MARGIN * 2;
const ACCENT_CYAN = '#00b8d9';
const CARD_BG = '#ffffff';
const CARD_BORDER = '#e2e8f0';
const TEXT_PRIMARY = '#0f172a';
const TEXT_MUTED = '#475569';
const CARD_BG_DARK = '#1e293b';
const CARD_BORDER_DARK = '#334155';
const TEXT_PRIMARY_DARK = '#f1f5f9';
const TEXT_MUTED_DARK = '#94a3b8';

interface FlightLogCarouselProps {
  entries: ExposureLogEntry[];
  onEntryPress?: (entry: ExposureLogEntry) => void;
  maxItems?: number;
  /** Dark mode: light text on dark cards */
  dark?: boolean;
}

function LogCard({
  entry,
  onPress,
  dark,
}: {
  entry: ExposureLogEntry;
  onPress?: () => void;
  dark?: boolean;
}) {
  const date = new Date(entry.timestamp);
  const mSv = (entry.doseMicroSv / 1_000_000).toFixed(2);

  return (
    <Pressable
      style={[styles.card, dark && { backgroundColor: CARD_BG_DARK, borderColor: CARD_BORDER_DARK }]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${entry.route}, ${mSv} mSv, ${format(date, 'MMM d')}. Tap for details`}
    >
      <Text style={[styles.cardRoute, dark && { color: TEXT_PRIMARY_DARK }]} numberOfLines={1}>
        {entry.route}
      </Text>
      <Text style={styles.cardDose}>{mSv} mSv</Text>
      {entry.doseRateMicroSvPerHour != null && (
        <Text style={[styles.cardRate, dark && { color: TEXT_MUTED_DARK }]}>
          {(entry.doseRateMicroSvPerHour).toFixed(2)} µSv/h
        </Text>
      )}
      <Text style={[styles.cardDate, dark && { color: TEXT_MUTED_DARK }]}>{format(date, 'MMM d, yyyy')}</Text>
      <Text style={[styles.tapHint, dark && { color: TEXT_MUTED_DARK }]}>Tap for details</Text>
    </Pressable>
  );
}

export function FlightLogCarousel({
  entries,
  onEntryPress,
  maxItems = 20,
  dark = false,
}: FlightLogCarouselProps) {
  const recentEntries = entries
    .filter((e) => new Date(e.timestamp).getFullYear() === new Date().getFullYear())
    .slice(0, maxItems)
    .reverse();

  const renderItem = useCallback(
    ({ item }: { item: ExposureLogEntry }) => (
      <View style={styles.itemWrapper}>
        <LogCard
          entry={item}
          onPress={onEntryPress ? () => onEntryPress(item) : undefined}
          dark={dark}
        />
      </View>
    ),
    [onEntryPress, dark]
  );

  const keyExtractor = useCallback((item: ExposureLogEntry) => item.id, []);

  if (recentEntries.length === 0) {
    return (
      <View style={[styles.emptyCardCompact, dark && { backgroundColor: CARD_BG_DARK, borderColor: CARD_BORDER_DARK }]}>
        <Text style={[styles.emptyTextCompact, dark && { color: TEXT_MUTED_DARK }]}>No flights yet — log one below</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={[styles.sectionLabel, dark && { color: TEXT_MUTED_DARK }]}>Recent exposures</Text>
      <FlatList
        data={recentEntries}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.listContent}
        snapToInterval={ITEM_WIDTH}
        snapToAlignment="start"
        decelerationRate="fast"
        {...(Platform.OS === 'web' && {
          // Web: use scroll event for snap
          onMomentumScrollEnd: undefined,
        })}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginBottom: 16 },
  sectionLabel: {
    fontSize: 12,
    color: TEXT_MUTED,
    marginBottom: 8,
    fontWeight: '600',
  },
  listContent: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  itemWrapper: {
    width: ITEM_WIDTH,
    paddingHorizontal: CARD_MARGIN,
  },
  card: {
    width: CARD_WIDTH,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    backgroundColor: CARD_BG,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#cbd5e1',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 6,
      elevation: 2,
    }),
  },
  cardRoute: {
    fontSize: 16,
    fontWeight: '700',
    color: TEXT_PRIMARY,
    marginBottom: 4,
  },
  cardDose: {
    fontSize: 20,
    fontWeight: '700',
    color: ACCENT_CYAN,
    marginBottom: 2,
  },
  cardRate: {
    fontSize: 12,
    color: TEXT_MUTED,
    marginBottom: 4,
  },
  cardDate: {
    fontSize: 12,
    color: TEXT_MUTED,
  },
  tapHint: {
    fontSize: 10,
    color: TEXT_MUTED,
    marginTop: 8,
    fontStyle: 'italic',
  },
  emptyCard: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
  },
  emptyCardCompact: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    backgroundColor: CARD_BG,
    marginBottom: 12,
  },
  emptyTextCompact: {
    fontSize: 12,
    color: TEXT_MUTED,
  },
  emptyText: {
    fontSize: 14,
    color: TEXT_MUTED,
  },
  emptyHint: {
    fontSize: 12,
    color: TEXT_MUTED,
    marginTop: 4,
  },
});
